import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ImageSquare, FilmSlate, Sparkle, House,
  ArrowsClockwise, DownloadSimple, ShareNetwork, WhatsappLogo,
  Wrench,
} from "@phosphor-icons/react";
import StaticTemplatePreview from "@/components/StaticTemplatePreview";
import { exportStaticAsPng, EXPORT_WIDTHS } from "@/lib/staticExport";
import { resolveManifestProperty } from "@/lib/manifestUrls";

/**
 * SocialStudioTabV2 — versione semplificata del Social Studio.
 *
 * Obiettivi (per richiesta utente iter 305 r11):
 *   1. **4 formati core** invece di 13: Post · Story · Reel · Prima/Dopo.
 *   2. **Niente layout/color selector** visibile — usa la scelta salvata
 *      sul manifest o default (elegante + verde_brera). Pro può "Personalizza"
 *      via accordion (TBD round successivo).
 *   3. **Layout a 2 colonne**: anteprima grande a sinistra, formati + azioni
 *      a destra. Niente scroll lungo prima di capire cosa fare.
 *   4. **Anteprime reali**: stessi endpoint del V1, ma senza i bug 16:9 del
 *      post (fixati lato backend con _fit_to_width).
 *
 * Riusa gli stessi endpoint server del V1, quindi nessuna modifica API.
 */
export default function SocialStudioTabV2({ propertyId }) {
  const [manifest, setManifest] = useState(null);
  const [manifestError, setManifestError] = useState(null);
  const [active, setActive] = useState("post"); // post|story|reel|before_after|occ_open_house|occ_venduto
  // iter v28 r474 — User M1385: «togli 16:9 ovunque nell'Editor social».
  // Rimosso linkedInMode (era il toggle "Versione 16:9 per LinkedIn"). Il
  // formato Post resta 1:1 (default Feed IG/FB).
  // iter 313 r21 v19 — Sezione "per occasione": 2 voci separate Open House
  // + Venduto, identiche al V1 (Annuncio → Pubblica → Social). Riusa
  // composer Python aggiungendo badge e foto leggermente oscurata.
  const [occasionFormat, setOccasionFormat] = useState("1x1"); // 1x1 (post) | 9x16 (story)
  const [occasionDate, setOccasionDate] = useState("");
  const [occasionTime, setOccasionTime] = useState("");
  const [occasionDays, setOccasionDays] = useState(14);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [busy, setBusy] = useState(false);
  // iter v33 r67 · Diagnostica reel: se il reel fallisce, l'utente clicca
  // "Diagnostica" → chiamiamo /reel-diagnostic e mostriamo l'output per
  // capire quale foto specifica sta rompendo ffmpeg.
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagnosticBusy, setDiagnosticBusy] = useState(false);
  // iter 313 r21 v5 — colore custom della banda reel (override locale, no save profile)
  const [reelBandColor, setReelBandColor] = useState(null); // null = usa default profilo agenzia
  const objectUrlRef = useRef(null);
  const staticPreviewRef = useRef(null);

  // Stile risolto dal manifest (con fallback)
  const staticLayout = manifest?.social_studio_style?.layout || "elegante";
  const staticColor = manifest?.social_studio_style?.color || "verde_brera";
  // iter 312 r4: Pro paga e niente watermark Pronto sui materiali
  const isPro = manifest?.is_pro || manifest?.plan === "pro";

  // ─── Fetch manifest ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setManifestError(null);
        const { data } = await api.get(`/properties/${propertyId}/social/manifest`);
        if (!cancelled) setManifest(data);
      } catch (err) {
        const msg = err?.response?.status === 404 ? "Immobile non trovato." : "Caricamento Editor social fallito.";
        if (!cancelled) {
          setManifestError(msg);
          toast.error(msg);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [propertyId]);

  // ─── Fetch asset quando cambia formato ────────────────────────────
  useEffect(() => {
    if (!manifest) return;
    if (active === "story") {
      // iter v28 r46 — Non revochiamo: vogliamo conservare in cache.
      setPreviewUrl(null);
      setLoading(false);
      return;
    }
    fetchAsset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, manifest?.property?.id, occasionFormat, occasionDate, occasionTime, occasionDays]);

  // iter 313 r21 v15 — quando cambia SOLO il colore banda del Reel,
  // mantieni il preview vecchio visibile con overlay loader (no flash bianco).
  // Skip al primo render (gestito già dal useEffect sopra).
  const lastBandColorRef = useRef(reelBandColor);
  useEffect(() => {
    if (!manifest || active !== "reel") {
      lastBandColorRef.current = reelBandColor;
      return;
    }
    if (lastBandColorRef.current === reelBandColor) return;
    lastBandColorRef.current = reelBandColor;
    fetchAsset({ keepPreview: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reelBandColor]);

  const revokeUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  // iter v28 r46 — Cache delle anteprime per riusare blob già generati.
  // Map<url, blobUrl>. Si svuota solo al cambio propertyId o smontaggio.
  const previewCacheRef = useRef(new Map());
  const cachedBlobsRef = useRef([]); // lista per la revoca finale

  // Pulizia totale al cambio propertyId / smontaggio
  useEffect(() => {
    return () => {
      cachedBlobsRef.current.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (_) {}
      });
      cachedBlobsRef.current = [];
      previewCacheRef.current.clear();
    };
  }, [propertyId]);

  const fetchAsset = async ({ keepPreview = false } = {}) => {
    // iter v33 r5 — User M1478: blocca generazione editor social quando un
    // sopralluogo è in lavorazione (Nano Banana già occupato dal kit).
    try {
      const flag = JSON.parse(localStorage.getItem("pronto_in_creazione_v1") || "null");
      if (flag?.pid) {
        toast.warning("Elaborazione in pausa", {
          description: "Sto preparando il tuo sopralluogo. L'editor social torna disponibile appena finisco (~4-6 min).",
          duration: 5000,
        });
        return;
      }
    } catch { /* localStorage non disponibile, procedi */ }

    setLoading(true);
    if (!keepPreview) {
      setPreviewUrl(null);
      // iter v28 r46 — Niente revokeUrl: il blob potrebbe essere
      // riusato se l'utente torna sullo stesso formato/opzioni.
    }
    setPreviewError(null);
    setDiagnostic(null);  // iter v33 r67 · reset diagnostic quando riproviamo
    try {
      let url;
      const aspect = active === "post"
        ? "1x1"
        : (active === "occ_open_house" || active === "occ_venduto")
        ? occasionFormat
        : "9x16";
      if (active === "post") {
        url = `/properties/${propertyId}/social/post.png?template=${staticColor}&aspect=${aspect}&layout=${staticLayout}`;
      } else if (active === "occ_open_house" || active === "occ_venduto") {
        // iter 313 r21 v19 — Per occasione = stesso composer Post/Story
        // + badge OPEN HOUSE / VENDUTO + foto leggermente oscurata.
        const params = new URLSearchParams({
          template: staticColor,
          aspect,
          layout: staticLayout,
          occasion: active === "occ_open_house" ? "open_house" : "venduto",
        });
        if (active === "occ_open_house") {
          if (occasionDate) {
            const [, mm, dd] = occasionDate.split("-");
            if (dd && mm) params.set("event_date", `${dd}/${mm}`);
          }
          if (occasionTime) params.set("event_time", occasionTime);
        } else {
          const n = Number(occasionDays);
          if (Number.isFinite(n) && n > 0) params.set("days_to_sell", String(Math.round(n)));
        }
        url = `/properties/${propertyId}/social/post.png?${params.toString()}`;
      } else if (active === "reel") {
        const bc = reelBandColor ? `&band_color=${encodeURIComponent(reelBandColor.replace("#", ""))}` : "";
        url = `/properties/${propertyId}/social/reel.mp4?template=${staticColor}&layout=${staticLayout}&aspect=${aspect}&show_price=false${bc}`;
        // iter v29 r065 — Reel via async job per bypassare 520 Cloudflare.
        // In produzione il render richiede 35-50s e il proxy taglia >30s.
        // Pattern: POST /start → polling /status → GET /reel.mp4 (cache hit 0s).
        if (previewCacheRef.current.has(url)) {
          setPreviewUrl(previewCacheRef.current.get(url));
          setLoading(false);
          return;
        }
        // iter v30 r100 — User M1750: «il reel non parte mai con 2 foto».
        // Funzione helper che avvia il job e ne fa il polling. In caso di
        // 404 sul polling (backend riavviato → job in memoria perso),
        // riavviamo automaticamente il job UNA volta e ricominciamo il
        // polling, invece di aspettare 120s inutili.
        const startUrl = url.replace("/reel.mp4?", "/reel/start?");
        const runJobWithPolling = async () => {
          const startResp = await api.post(startUrl, null, { timeout: 15000 });
          const sd = startResp.data || {};
          if (sd.status !== "ready" && sd.status !== "running") {
            return { ok: false, error: "Generazione reel non avviata. Riprova fra poco." };
          }
          if (!sd.job_id) return { ok: true }; // cache hit immediato
          const maxAttempts = 60; // 60 × 2s = 120s max
          let consecutiveTimeouts = 0;
          for (let i = 0; i < maxAttempts; i++) {
            await new Promise((r) => setTimeout(r, 2000));
            // iter v33 r48 · Idem SocialStudioTab: 20s per-poll + skip
            // fino a 3 timeout consecutivi prima di dare per persa la
            // generazione. Il server continua a renderizzare comunque.
            let sResp;
            try {
              sResp = await api.get(`/social/reel-jobs/${sd.job_id}`, {
                timeout: 20000, validateStatus: () => true,
              });
              consecutiveTimeouts = 0;
            } catch (pollErr) {
              consecutiveTimeouts += 1;
              if (consecutiveTimeouts >= 3) {
                return { ok: false, error: "Rete instabile. Il reel sta ancora venendo generato, riprova fra 30 secondi." };
              }
              continue;
            }
            const s = sResp.data || {};
            if (sResp.status === 404) {
              // Job sparito (backend riavviato): segnala al caller di riavviare
              return { ok: false, restart: true };
            }
            if (s.status === "ready") return { ok: true };
            if (s.status === "failed") {
              return { ok: false, error: s.error_detail || "Generazione reel non riuscita." };
            }
          }
          return { ok: false, error: "Sta impiegando troppo tempo. Riprova fra un minuto." };
        };
        try {
          let result = await runJobWithPolling();
          // Se il job è stato perso (404), riproviamo UNA volta a riavviarlo
          if (!result.ok && result.restart) {
            result = await runJobWithPolling();
            if (!result.ok && result.restart) {
              setPreviewError("Il servizio si sta riavviando. Riprova fra 10 secondi.");
              setLoading(false);
              return;
            }
          }
          if (!result.ok) {
            setPreviewError(result.error || "Generazione reel non riuscita");
            setLoading(false);
            return;
          }
          // Scarica dalla cache (hit → 0s)
          const resp = await api.get(url, { responseType: "blob", validateStatus: () => true, timeout: 30000 });
          if (resp.status >= 200 && resp.status < 300) {
            const blobUrl = URL.createObjectURL(resp.data);
            cachedBlobsRef.current.push(blobUrl);
            objectUrlRef.current = blobUrl;
            previewCacheRef.current.set(url, blobUrl);
            setPreviewUrl(blobUrl);
            setLoading(false);
            return;
          }
          setPreviewError("Reel pronto ma non scaricabile. Aggiorna la pagina e riprova.");
          setLoading(false);
          return;
        } catch (err) {
          setPreviewError("Connessione interrotta durante la generazione. Riprova.");
          setLoading(false);
          return;
        }
      } else if (active === "before_after") {
        url = `/properties/${propertyId}/social/before-after.mp4?template=${staticColor}&layout=${staticLayout}&label_kind=prima_dopo&aspect=${aspect}`;
      } else {
        return;
      }
      // iter v28 r46 — Cache hit: mostra il blob istantaneamente
      if (previewCacheRef.current.has(url)) {
        setPreviewUrl(previewCacheRef.current.get(url));
        setLoading(false);
        return;
      }
      // iter 305 r11: axios con validateStatus:true → non triggera reject su
      // 4xx/5xx → niente "Failed to read responseText" page error con blob.
      // iter v28 r448 — Override timeout per video (reel ~45-90s, before_after ~30s)
      const isVideo = active === "reel" || active === "before_after";
      const resp = await api.get(url, {
        responseType: "blob",
        validateStatus: () => true,
        timeout: isVideo ? 180000 : 60000,
      });
      if (resp.status >= 200 && resp.status < 300) {
        const blobUrl = URL.createObjectURL(resp.data);
        // iter v28 r46 — NON revochiamo più il blob precedente: lo
        // teniamo in cache per il riuso istantaneo. La pulizia avviene
        // solo al cambio propertyId / smontaggio.
        cachedBlobsRef.current.push(blobUrl);
        objectUrlRef.current = blobUrl;
        previewCacheRef.current.set(url, blobUrl);
        setPreviewUrl(blobUrl);
        return;
      }
      // Estrai il detail dal blob JSON di errore (response.data è un Blob)
      let detail = `Errore ${resp.status}. Riprova fra poco.`;
      try {
        const txt = await resp.data.text();
        try { detail = JSON.parse(txt)?.detail || detail; } catch { /* non JSON */ }
      } catch { /* ignore */ }
      if (resp.status >= 500 && (active === "reel" || active === "before_after")) {
        detail = "Il video è temporaneamente non disponibile. Riprova fra qualche minuto.";
      }
      setPreviewError(detail);
      // Errore: svuota preview così l'utente vede il messaggio invece del vecchio video
      if (keepPreview) {
        setPreviewUrl(null);
        revokeUrl();
      }
    } catch (err) {
      setPreviewError("Anteprima non disponibile. Controlla la connessione e riprova.");
      if (keepPreview) {
        setPreviewUrl(null);
        revokeUrl();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => () => revokeUrl(), []);

  // iter v33 r67 · Diagnostica reel: chiamata quando l'utente clicca
  // "Diagnostica" dopo un errore di generazione. Restituisce info per
  // ogni foto (fetch OK, PIL OK, ffprobe OK) cosi' si capisce esattamente
  // quale foto sta rompendo ffmpeg.
  const runReelDiagnostic = async () => {
    if (!propertyId) return;
    setDiagnosticBusy(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}/social/reel-diagnostic`, {
        timeout: 30000,
      });
      setDiagnostic(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Diagnostica fallita";
      setDiagnostic({
        photos_count: 0,
        photos: [],
        recent_failures: [{ attempt: "diagnostic_endpoint", error: String(msg) }],
      });
    } finally {
      setDiagnosticBusy(false);
    }
  };

  const baAvailable = manifest?.formats?.before_after?.available !== false;

  const property = manifest?.property;

  // ─── Definizione formati visibili ──────────────────────────────────
  // iter v28 r454 — User M1370: nascondi "Per occasione" sugli affitti.
  const _isAffittoV2 = (manifest?.property?.listing_type || "").toLowerCase() === "affitto";
  const FORMATS = useMemo(() => {
    const base = [
      { key: "post", label: "Post", Icon: ImageSquare, hint: "Quadrato per Instagram, Facebook" },
      { key: "story", label: "Story", Icon: Sparkle, hint: "9:16 verticale per Stories" },
      { key: "reel", label: "Reel video", Icon: FilmSlate, hint: "Video 9:16 cinematico" },
      { key: "before_after", label: "Prima / Dopo", Icon: ArrowsClockwise, hint: baAvailable ? "Video trasformazione" : "Serve almeno 1 foto trasformata", disabled: !baAvailable },
    ];
    if (_isAffittoV2) return base;
    return [
      ...base,
      { key: "occ_open_house", label: "Open House", Icon: Sparkle, hint: "Post o Story · porte aperte" },
      { key: "occ_venduto",    label: "Venduto",    Icon: Sparkle, hint: "Post o Story · social proof" },
    ];
  }, [baAvailable, _isAffittoV2]);

  // ─── Azioni Scarica / Condividi ────────────────────────────────────
  const filenameFor = (ext) => {
    const slug = (manifest?.property?.title || "annuncio").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
    return `pronto-${slug}-${active}.${ext}`;
  };

  const onDownload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (active === "story") {
        const node = staticPreviewRef.current;
        if (!node) throw new Error("Anteprima non pronta");
        await exportStaticAsPng({ node, targetWidth: EXPORT_WIDTHS.story_nuovo || 1080, filename: filenameFor("png") });
        toast.success("Story salvata");
      } else if (previewUrl) {
        // iter 313 r21 v8 — Mobile fix: l'attributo `<a download>` su iOS
        // Safari/Chrome non funziona con blob URL remoti → l'utente vede
        // solo "anteprima inline" senza salvare. SOLUZIONE: fetchamo il blob,
        // creiamo blob URL locale, e se siamo su mobile preferiamo la Web
        // Share API (che permette "Salva in Foto" o "Salva in File"). Su
        // desktop fallback al download classico.
        const ext = active === "reel" || active === "before_after" ? "mp4" : "png";
        const mime = ext === "mp4" ? "video/mp4" : "image/png";
        const filename = filenameFor(ext);
        const r = await fetch(previewUrl);
        const blob = await r.blob();
        const file = new File([blob], filename, { type: mime });
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
        if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: manifest?.property?.title || "", text: "" });
          toast.success("Salva in Foto / File dal menù condividi");
        } else {
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
          toast.success("Salvato");
        }
      } else {
        toast.error("Anteprima non ancora pronta");
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Download non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const onShareNative = async () => {
    if (busy) return;
    setBusy(true);
    try {
      let blob, mime, filename;
      if (active === "story") {
        const node = staticPreviewRef.current;
        if (!node) throw new Error("Anteprima non pronta");
        const { captureAsPng } = await import("@/lib/staticExport");
        blob = await captureAsPng(node, EXPORT_WIDTHS.story_nuovo || 1080);
        mime = "image/png"; filename = filenameFor("png");
      } else if (previewUrl) {
        const r = await fetch(previewUrl);
        blob = await r.blob();
        if (active === "reel" || active === "before_after") {
          mime = "video/mp4"; filename = filenameFor("mp4");
        } else {
          mime = "image/png"; filename = filenameFor("png");
        }
      } else {
        toast.error("Anteprima non ancora pronta");
        return;
      }
      const file = new File([blob], filename, { type: mime });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: manifest?.property?.title, text: "" });
        toast.success("Condividi su WhatsApp o Instagram dal selettore");
      } else {
        // Desktop fallback: download + apri WhatsApp Web
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        window.open(`https://wa.me/?text=${encodeURIComponent(manifest?.property?.title || "")}`, "_blank", "noopener,noreferrer");
        toast.success("File salvato · allegalo su WhatsApp Web");
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Condivisione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────
  if (manifestError) {
    return (
      <div className="p-8 text-center text-[13px] text-[#9A968B]" data-testid="ss-v2-error">
        {manifestError}
      </div>
    );
  }
  if (!manifest) {
    return (
      <div className="p-8 text-center text-[13px] text-[#9A968B]" data-testid="ss-v2-loading">
        Caricamento…
      </div>
    );
  }

  const isStatic = active === "story";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="social-studio-v2">
      {/* COLONNA SINISTRA — Anteprima grande sticky */}
      <div className="lg:col-span-7">
        <div className="sticky top-4">
          <div className="bg-[#F8F7F2] border border-[#E5E7E0] rounded-xl p-4 sm:p-6 min-h-[480px] flex items-center justify-center" data-testid="ss-v2-preview-frame">
            {isStatic ? (
              <div
                ref={staticPreviewRef}
                className={`mx-auto w-full ${active === "story" ? "max-w-[260px]" : "max-w-[420px]"}`}
                data-testid={`ss-v2-static-${active}`}
              >
                <StaticTemplatePreview
                  template={active === "story" ? "story_nuovo" : active}
                  layout={staticLayout}
                  color={staticColor}
                  property={resolveManifestProperty(property)}
                  agency={manifest.agency}
                  showWatermark={!isPro}
                />
              </div>
            ) : loading && !previewUrl ? (
              <div className="flex flex-col items-center gap-2.5">
                <div className="w-9 h-9 rounded-full border-3 border-[#7C8B6E] border-t-transparent animate-spin" data-testid="ss-v2-spinner" />
                {active === "reel" && (
                  <p className="text-[11.5px] text-[#6B6A65] leading-snug text-center max-w-[220px]">Sto preparando il reel<br/>(prima generazione 25-35s)</p>
                )}
              </div>
            ) : previewError ? (
              <div className="text-center max-w-[340px] px-4" data-testid="ss-v2-preview-error">
                <House size={36} weight="thin" className="text-[#C8C5BD] mx-auto mb-2" />
                <div className="text-[13px] text-[#1C1C1A] font-semibold mb-1">Anteprima non disponibile</div>
                <div className="text-[12px] text-[#6B6A65] leading-snug mb-3">{previewError}</div>
                <div className="flex flex-col items-center gap-2">
                  <button
                    onClick={() => fetchAsset()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#F1F4ED] text-[11.5px] font-semibold transition-colors"
                    data-testid="ss-v2-retry"
                  >
                    <ArrowsClockwise size={12} weight="bold" /> Riprova
                  </button>
                  {/* iter v33 r67 · Diagnostica: se il reel fallisce, tocca
                      qui per far analizzare foto per foto e capire quale
                      rompe ffmpeg. Utile per debug remoto: l'agente fa
                      screenshot dell'output e ce lo manda. */}
                  {active === "reel" && (
                    <button
                      onClick={runReelDiagnostic}
                      disabled={diagnosticBusy}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#F5F4F0] hover:bg-[#EDEBE4] text-[#5A5651] text-[10.5px] transition-colors disabled:opacity-50"
                      data-testid="ss-v2-diagnostic"
                    >
                      <Wrench size={11} weight="bold" />
                      {diagnosticBusy ? "Analizzo le foto..." : "Diagnostica (perche' non parte)"}
                    </button>
                  )}
                  {diagnostic && (
                    <div className="mt-2 w-full max-w-[320px] bg-[#FAFAF7] border border-[#E5E7E0] rounded-md p-2.5 text-left" data-testid="ss-v2-diagnostic-output">
                      <div className="text-[10px] font-bold text-[#1C1C1A] uppercase tracking-wide mb-1.5">
                        Diagnostica ({diagnostic.photos_count} foto)
                      </div>
                      {diagnostic.photos.map((ph) => {
                        const allOk = ph.fetch_ok && ph.pil_ok && ph.ffprobe_ok;
                        return (
                          <div key={ph.index} className={`text-[10.5px] py-1 border-b border-[#EDEBE4] last:border-0 ${allOk ? "text-[#5A6A4D]" : "text-[#B84A3B]"}`}>
                            <div className="font-semibold">
                              Foto {ph.index + 1}: {allOk ? "OK" : "PROBLEMA"}
                              {ph.size_kb ? ` · ${ph.size_kb} KB` : ""}
                              {ph.pil_size ? ` · ${ph.pil_size[0]}x${ph.pil_size[1]}` : ""}
                            </div>
                            {!ph.fetch_ok && <div>× Impossibile scaricare: {ph.fetch_error}</div>}
                            {ph.fetch_ok && !ph.pil_ok && <div>× PIL error: {ph.pil_error}</div>}
                            {ph.pil_ok && !ph.ffprobe_ok && <div>× ffprobe error: {ph.ffprobe_error}</div>}
                          </div>
                        );
                      })}
                      {diagnostic.recent_failures?.length > 0 && (
                        <div className="mt-2 pt-1.5 border-t border-[#E5E7E0]">
                          <div className="text-[9.5px] font-bold text-[#8A8580] uppercase tracking-wide">Ultimi errori</div>
                          {diagnostic.recent_failures.slice(0, 2).map((f, i) => (
                            <div key={i} className="text-[9.5px] text-[#8A8580] mt-1 break-words">
                              <strong>{f.attempt}:</strong> {(f.error || "").substring(0, 180)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : !previewUrl ? (
              <div className="w-9 h-9 rounded-full border-3 border-[#7C8B6E] border-t-transparent animate-spin" />
            ) : active === "reel" || active === "before_after" ? (
              // iter 313 r21 v15 — Overlay loader sopra al video durante
              // la rigenerazione (cambio band_color): l'utente vede chiaramente
              // che sta lavorando e non perde il contesto del preview.
              <div className="relative inline-block">
                <video
                  src={previewUrl}
                  autoPlay loop muted playsInline
                  className={`rounded-md w-full ${active === "reel" ? "max-w-[300px] aspect-[9/16]" : "max-w-[300px] aspect-[9/16]"} object-contain transition-opacity ${loading ? "opacity-40" : "opacity-100"}`}
                  data-testid={`ss-v2-video-${active}`}
                />
                {loading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/35 rounded-md" data-testid="ss-v2-video-loader">
                    <div className="w-10 h-10 rounded-full border-3 border-white border-t-transparent animate-spin" />
                    <span className="text-[11px] text-white font-medium px-2 py-1 rounded bg-black/40">Aggiornamento colore…</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative inline-block">
                <img
                  src={previewUrl}
                  alt=""
                  className={`rounded-md w-full ${
                    (active === "occ_open_house" || active === "occ_venduto") && occasionFormat === "9x16"
                      ? "max-w-[260px] aspect-[9/16]"
                      : "max-w-[420px] aspect-square"
                  } object-contain transition-opacity ${loading ? "opacity-40" : "opacity-100"}`}
                  data-testid="ss-v2-image"
                />
                {loading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/35 rounded-md">
                    <div className="w-10 h-10 rounded-full border-3 border-white border-t-transparent animate-spin" />
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="mt-2 text-[11px] text-[#9A968B] text-center leading-snug">
            Stile <strong>{staticLayout}</strong> · colore <strong>{staticColor.replace("_", " ")}</strong> ·
            cambialo dal tuo profilo agenzia
          </p>
        </div>
      </div>

      {/* COLONNA DESTRA — Formati + Azioni */}
      <div className="lg:col-span-5 space-y-5">
        <div>
          <h3 className="font-editorial text-[20px] text-[#1C1C1A] mb-1">Crea contenuto social</h3>
          <p className="text-[12.5px] text-[#6B6A65] leading-snug">
            Scegli un formato. L'anteprima si aggiorna a sinistra.
          </p>
        </div>

        {/* Formati */}
        <div className="space-y-2" data-testid="ss-v2-formats">
          {FORMATS.map(({ key, label, Icon, hint, disabled }) => (
            <button
              key={key}
              onClick={() => !disabled && setActive(key)}
              disabled={disabled}
              className={`w-full text-left flex items-center gap-3 p-3 rounded-md border transition-all ${
                disabled
                  ? "opacity-50 cursor-not-allowed border-[#EAE7DF] bg-white"
                  : active === key
                  ? "border-[#7C8B6E] bg-[#F1F4ED] shadow-sm"
                  : "border-[#EAE7DF] bg-white hover:border-[#C8CDB8]"
              }`}
              data-testid={`ss-v2-fmt-${key}`}
            >
              <div className={`w-9 h-9 rounded-md flex-shrink-0 flex items-center justify-center ${
                active === key ? "bg-[#7C8B6E] text-white" : "bg-[#F1F4ED] text-[#7C8B6E]"
              }`}>
                <Icon size={16} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-[#1C1C1A]">{label}</div>
                <div className="text-[11.5px] text-[#9A968B] truncate">{hint}</div>
              </div>
            </button>
          ))}
        </div>

        {/* iter v28 r474 — Toggle "Versione 16:9 per LinkedIn" rimosso
            (user M1385: togli 16:9 ovunque). Post resta 1:1. */}

        {/* iter 313 r21 v19 — Pannello "Per occasione" (Open House / Venduto) */}
        {(active === "occ_open_house" || active === "occ_venduto") && (
          <div
            className="p-3 rounded-md border border-[#EAE7DF] bg-white space-y-3"
            data-testid="ss-v2-occasione-panel"
          >
            {/* Formato di output */}
            <div>
              <div className="text-[12px] font-semibold text-[#1C1C1A] mb-1.5">Formato</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "1x1", label: "Post 1:1" },
                  { v: "9x16", label: "Story 9:16" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setOccasionFormat(opt.v)}
                    className={`px-3 py-2 rounded-md border text-[12.5px] font-semibold transition-colors ${
                      occasionFormat === opt.v
                        ? "border-[#7C8B6E] bg-[#F1F4ED] text-[#1C1C1A]"
                        : "border-[#EAE7DF] bg-white text-[#6B6A65] hover:border-[#C8CDB8]"
                    }`}
                    data-testid={`ss-v2-occasione-fmt-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Campi specifici */}
            {active === "occ_open_house" ? (
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[11px] font-semibold text-[#1C1C1A] block mb-1">Data evento</span>
                  <input
                    type="date"
                    value={occasionDate}
                    onChange={(e) => setOccasionDate(e.target.value)}
                    className="w-full px-2.5 py-2 rounded-md border border-[#EAE7DF] bg-white text-[12.5px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E]"
                    data-testid="ss-v2-occasione-date"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold text-[#1C1C1A] block mb-1">Orario</span>
                  <input
                    type="time"
                    value={occasionTime}
                    onChange={(e) => setOccasionTime(e.target.value)}
                    className="w-full px-2.5 py-2 rounded-md border border-[#EAE7DF] bg-white text-[12.5px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E]"
                    data-testid="ss-v2-occasione-time"
                  />
                </label>
              </div>
            ) : (
              <label className="block">
                <span className="text-[11px] font-semibold text-[#1C1C1A] block mb-1">Giorni dalla pubblicazione al rogito</span>
                <input
                  type="number"
                  min="1"
                  max="999"
                  value={occasionDays}
                  onChange={(e) => setOccasionDays(e.target.value)}
                  className="w-full px-2.5 py-2 rounded-md border border-[#EAE7DF] bg-white text-[12.5px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E]"
                  data-testid="ss-v2-occasione-days"
                />
              </label>
            )}

            <p className="text-[10.5px] text-[#9A968B] leading-snug">
              {active === "occ_open_house"
                ? "Stesso layout del Post / Story con foto leggermente più scura e badge OPEN HOUSE in alto."
                : "Stesso layout del Post / Story con badge VENDUTO IN N GIORNI per celebrare la chiusura."}
            </p>
          </div>
        )}

        {/* iter 313 r21 v5 — Color picker compatto per la banda del Reel */}
        {active === "reel" && (
          <div
            className="p-3 rounded-md border border-[#EAE7DF] bg-white"
            data-testid="ss-v2-reel-band-color"
          >
            <div className="text-[12px] font-semibold text-[#1C1C1A] mb-2">Colore banda</div>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { v: null,      hex: "#7C8B6E", label: "Default" },
                { v: "F0EBE0",  hex: "#F0EBE0", label: "Crema" },
                { v: "FFFFFF",  hex: "#FFFFFF", label: "Bianco" },
                { v: "0F0F0F",  hex: "#0F0F0F", label: "Nero" },
                { v: "1C1C1A",  hex: "#1C1C1A", label: "Notte" },
                { v: "C8A35A",  hex: "#C8A35A", label: "Oro" },
                { v: "D72638",  hex: "#D72638", label: "Rosso" },
              ].map((opt) => {
                const selected = (reelBandColor || null) === opt.v;
                const isWhite = opt.hex.toLowerCase() === "#ffffff";
                return (
                  <button
                    key={String(opt.v)}
                    type="button"
                    onClick={() => setReelBandColor(opt.v)}
                    title={opt.label}
                    aria-label={`Banda ${opt.label}`}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      selected
                        ? "border-[#1C1C1A] ring-2 ring-[#7C8B6E]/40 scale-110"
                        : `${isWhite ? "border-[#9A968B]" : "border-white"} shadow-sm hover:scale-105`
                    }`}
                    style={{ backgroundColor: opt.hex }}
                    data-testid={`ss-v2-band-color-${opt.v || "default"}`}
                  />
                );
              })}
              <label
                className="ml-1 w-8 h-8 rounded-full border-2 border-dashed border-[#C8CDB8] flex items-center justify-center text-[10px] text-[#7C8B6E] hover:border-[#7C8B6E] cursor-pointer"
                title="Colore personalizzato"
                data-testid="ss-v2-band-color-custom-wrap"
              >
                <input
                  type="color"
                  value={reelBandColor ? `#${reelBandColor.replace("#", "")}` : "#7C8B6E"}
                  onChange={(e) => setReelBandColor(e.target.value.replace("#", "").toUpperCase())}
                  className="opacity-0 w-0 h-0 absolute"
                  data-testid="ss-v2-band-color-picker"
                />
                +
              </label>
            </div>
            <p className="text-[10.5px] text-[#9A968B] mt-2 leading-snug">
              Cambia il colore della striscia in basso. Default usa il colore della tua agenzia.
            </p>
          </div>
        )}

        {/* Azioni */}
        <div className="grid grid-cols-2 gap-2.5" data-testid="ss-v2-actions">
          <button
            onClick={onShareNative}
            disabled={busy || (!isStatic && !previewUrl)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#7C8B6E] hover:bg-[#90A082] text-white text-[13px] font-semibold transition-colors disabled:opacity-50"
            data-testid="ss-v2-share-btn"
          >
            <ShareNetwork size={15} weight="fill" /> Condividi
          </button>
          <button
            onClick={onDownload}
            disabled={busy || (!isStatic && !previewUrl)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-white border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#F1F4ED] text-[13px] font-medium transition-colors disabled:opacity-50"
            data-testid="ss-v2-download-btn"
          >
            <DownloadSimple size={15} weight="bold" /> Scarica
          </button>
        </div>

        {/* WhatsApp shortcut */}
        <button
          onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent((manifest?.property?.title || "") + " — " + (window.location.origin))}`, "_blank", "noopener,noreferrer")}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[#25D366] hover:bg-[#1DAF52] text-white text-[12.5px] font-semibold transition-colors"
          data-testid="ss-v2-whatsapp-btn"
        >
          <WhatsappLogo size={14} weight="fill" /> Apri WhatsApp · invia il link
        </button>

        {/* Hint: come tornare al vecchio Studio */}
        <div className="pt-3 mt-3 border-t border-[#EAE7DF] text-[11px] text-[#9A968B] text-center">
          Versione semplificata · <button
            type="button"
            onClick={() => {
              const params = new URLSearchParams(window.location.search);
              params.set("v2", "0");
              window.location.search = params.toString();
            }}
            className="underline text-[#7C8B6E] hover:text-[#5d6952]"
            data-testid="ss-v2-go-old"
          >torna alla versione completa</button>
        </div>
      </div>
    </div>
  );
}
